import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {
  getCurrentRepositoryNwo,
  getNavigationTabCountAuthoritativeVersion,
  getNavigationTabCountSnapshot,
  getNavigationTabCountTargetVersion,
  updateNavigationTabCount,
} from '@github-ui/global-navigation'
import {useMutation} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useIssueCommentsActions, usePullRequestBasePath} from '../stores/selectors'
import type {IssueComment} from '../page-data/payloads/issue-comments'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  conversationCommentsQueryKey,
  pushConversationItemToQueryData,
} from '../page-data/loaders/use-conversation-comments'
import {buildConversationIssueComment} from '../helpers/build-conversation-comments'

export type CreateTimelineCommentMutationVariables =
  | {text: string; closePullRequest?: never; reopenPullRequest?: never}
  | {text: string; closePullRequest: true; reopenPullRequest?: never}
  | {text: string; closePullRequest?: never; reopenPullRequest: true}

type MutationResponse = {
  message: string
  commentId: number
  issueComment: IssueComment
}

export function useCreatePullRequestTimelineCommentMutation() {
  const basePath = usePullRequestBasePath()
  const apiUrl = `${basePath}/page_data/${PageData.submitPullRequestTimelineComment}`
  const {addIssueComment} = useIssueCommentsActions()
  // TODO: add the store calls that we will eventually need to update the UI
  // with the information received about the comment

  return useMutation({
    mutationFn: async (variables: CreateTimelineCommentMutationVariables) => {
      const result = await reactFetchJSON(`${apiUrl}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: variables,
      })

      const json = await parseJSONWithBetterErrors(result)
      throwErrorsIfBadResponse(result, json)
      return json as MutationResponse
    },
    onMutate: () => ({
      repositoryNwo: getCurrentRepositoryNwo(),
      tabCount: getNavigationTabCountSnapshot('pull-requests'),
      targetVersion: getNavigationTabCountTargetVersion('pull-requests'),
      updateVersion: getNavigationTabCountAuthoritativeVersion('pull-requests'),
    }),
    onSuccess: (data, inputs, {repositoryNwo, tabCount, targetVersion, updateVersion}, context) => {
      if (isFeatureEnabled('prs_new_conversation_comments_api')) {
        pushConversationItemToQueryData(context.client, basePath, buildConversationIssueComment(data.issueComment))
        // buildConversationIssueComment has to fill in the blanks for unknown data, so we still need to refetch the
        // conversation to get an accurate comment. We can remove this once we migrate to a conversation-specific API
        // where the full data is available, which will allow us to avoid refetching the whole conversation.
        context.client.invalidateQueries({queryKey: conversationCommentsQueryKey(basePath), exact: true})
      } else {
        addIssueComment(data.issueComment)
      }
      if (inputs.closePullRequest) {
        updateNavigationTabCount(
          'pull-requests',
          {
            type: 'delta',
            delta: -1,
            ...(tabCount?.exact ? {expectedCountAfterAuthoritativeUpdate: tabCount.count} : {}),
          },
          {
            repositoryNwo,
            expectedUpdateVersion: updateVersion,
            expectedTargetVersion: targetVersion,
          },
        )
      } else if (inputs.reopenPullRequest) {
        updateNavigationTabCount(
          'pull-requests',
          {
            type: 'delta',
            delta: 1,
            ...(tabCount?.exact ? {expectedCountAfterAuthoritativeUpdate: tabCount.count} : {}),
          },
          {
            repositoryNwo,
            expectedUpdateVersion: updateVersion,
            expectedTargetVersion: targetVersion,
          },
        )
      }
    },
  })
}
